import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck, XCircle, FileText, Mail, Phone, User } from "lucide-react";
import { toast } from "sonner";

export default function AdminSchoolClaims() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [busy, setBusy] = useState({});
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    const { data } = await api.get(`${V1}/admin/school-claims?status=${status}`);
    setItems(data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => {
    setBusy((s) => ({ ...s, [id]: true }));
    try {
      await api.post(`${V1}/admin/school-claims/${id}/approve`);
      toast.success("Sahiplik onaylandı, kullanıcı okul yöneticisi oldu");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusy((s) => ({ ...s, [id]: false })); }
  };

  const confirmReject = async () => {
    if (!rejectId) return;
    setBusy((s) => ({ ...s, [rejectId]: true }));
    try {
      const { data } = await api.post(`${V1}/admin/school-claims/${rejectId}/reject`, { reason: rejectReason.trim() });
      if (data.email_sent) toast.success("Talep reddedildi ve bilgi e-postası gönderildi ✉️");
      else toast.warning(`Talep reddedildi. E-posta gönderilemedi: ${data.email_error || "?"}`);
      setRejectId(null); setRejectReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusy((s) => ({ ...s, [rejectId]: false })); }
  };

  return (
    <div className="space-y-6" data-testid="admin-claims-page">
      <div>
        <div className="overline text-accent">Sahiplik</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><ShieldCheck className="h-7 w-7" /> Okul Sahiplik Talepleri</h1>
      </div>
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="claims-tab-pending">Bekleyen</TabsTrigger>
          <TabsTrigger value="approved" data-testid="claims-tab-approved">Onaylı</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="claims-tab-rejected">Reddedilen</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="space-y-3">
        {items.length === 0 && <div className="p-12 text-center text-muted-foreground bg-card border border-border rounded-xl">Talep yok.</div>}
        {items.map((c) => (
          <div key={c.id} className="p-5 rounded-xl border border-border bg-card" data-testid={`claim-row-${c.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[280px]">
                <div className="font-display font-bold text-lg">{c.school?.name || "?"}</div>
                <div className="text-xs text-muted-foreground">Okul ID: {c.school_id}</div>
                <div className="mt-3 space-y-1 text-sm">
                  <div className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {c.user_name}</div>
                  <div className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {c.user_email}</div>
                  <div className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {c.user_phone}</div>
                  <div className="mt-2 p-3 rounded bg-muted/40 space-y-1">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Doğrulama</div>
                    <div className="text-sm flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" /> {c.official_email}
                      {c.email_domain_ok && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-success/10 text-success">Domain OK</span>}
                    </div>
                    {c.document_url && (
                      <div className="text-sm flex items-center gap-1">
                        <FileText className="h-3.5 w-3.5" />
                        <a href={c.document_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">Belgeyi Görüntüle</a>
                      </div>
                    )}
                    {c.note && <div className="text-xs text-muted-foreground">Not: {c.note}</div>}
                  </div>
                  {c.status === "rejected" && c.rejection_reason && (
                    <div className="mt-2 p-2 rounded bg-destructive/10 text-xs text-destructive">
                      <b>Ret sebebi:</b> {c.rejection_reason}
                      {c.reject_email_sent && <div className="mt-1 text-[11px]">✉️ Bilgi e-postası gönderildi</div>}
                    </div>
                  )}
                </div>
              </div>
              {status === "pending" && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => approve(c.id)} disabled={busy[c.id]} data-testid={`approve-claim-${c.id}`}>
                    <ShieldCheck className="h-4 w-4" /> Onayla
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setRejectId(c.id); setRejectReason(""); }} data-testid={`reject-claim-${c.id}`}>
                    <XCircle className="h-4 w-4" /> Reddet
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!rejectId} onOpenChange={(o) => !o && setRejectId(null)}>
        <DialogContent className="max-w-md" data-testid="reject-claim-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><XCircle className="h-5 w-5 text-destructive" /> Sahiplik Talebini Reddet</DialogTitle>
            <DialogDescription>
              Kullanıcıya iletilecek ret sebebini yazın. E-posta olarak gönderilecektir.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Örn: Yüklediğiniz belge okuduğumuz kadar okulun MEB izin belgesiyle eşleşmedi. Lütfen güncel PDF'i gönderin."
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectId(null)}>Vazgeç</Button>
            <Button variant="destructive" onClick={confirmReject} disabled={busy[rejectId]} data-testid="confirm-reject-btn">
              <XCircle className="h-4 w-4" /> Reddet ve E-posta Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
